import { Schema } from "effect";
import { compareUtf8Text, utf8ByteLength } from "./utf8";

export const DesktopSshFileBoundaryParseOptions = {
  errors: "all",
  onExcessProperty: "error",
  reportInput: false,
} as const;

export const DESKTOP_SSH_MAX_ALIAS_BYTES = 255;
export const DESKTOP_SSH_MAX_SCOPE_ID_BYTES = 180;
export const DESKTOP_SSH_MAX_ROOT_BYTES = 2_048;
export const DESKTOP_SSH_MAX_RELATIVE_PATH_BYTES = 2_048;
export const DESKTOP_SSH_MAX_NAME_BYTES = 512;
export const DESKTOP_SSH_MAX_LIST_LIMIT = 200;
export const DESKTOP_SSH_MAX_TEXT_BYTES = 256 * 1_024;
export const DESKTOP_SSH_MAX_DISCOVERED_HOSTS = 256;

const hasAsciiControlCharacter = (value: string): boolean => {
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint <= 0x1f || codePoint === 0x7f) return true;
  }
  return false;
};

const CanonicalBoundedTextSchema = (maximumBytes: number) =>
  Schema.String.check(
    Schema.isMinLength(1),
    Schema.makeFilter(
      (value) =>
        (value === value.trim() &&
          !hasAsciiControlCharacter(value) &&
          utf8ByteLength(value) <= maximumBytes) ||
        `must be canonical text of at most ${maximumBytes} UTF-8 bytes`,
    ),
  );

const BoundedPathTextSchema = (maximumBytes: number, allowEmpty = false) =>
  Schema.String.check(
    Schema.makeFilter(
      (value) =>
        ((allowEmpty || value.length > 0) &&
          !hasAsciiControlCharacter(value) &&
          utf8ByteLength(value) <= maximumBytes) ||
        `must be path text of at most ${maximumBytes} UTF-8 bytes`,
    ),
  );

const NonNegativeSafeIntegerSchema = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(0),
  Schema.isLessThanOrEqualTo(Number.MAX_SAFE_INTEGER),
);

const isCanonicalPathSegment = (segment: string): boolean =>
  segment !== "" &&
  segment !== "." &&
  segment !== ".." &&
  !hasAsciiControlCharacter(segment) &&
  utf8ByteLength(segment) <= DESKTOP_SSH_MAX_NAME_BYTES;

const isCanonicalPath = (value: string, allowEmpty: boolean): boolean => {
  if (value === "") return allowEmpty;
  if (value.startsWith("/") || value.includes("\\")) return false;
  return value.split("/").every(isCanonicalPathSegment);
};

const isCanonicalAbsoluteRoot = (value: string): boolean => {
  if (!value.startsWith("/") || value === "/" || value.includes("\\")) return false;
  return value
    .slice(1)
    .split("/")
    .every((segment) => segment !== "" && segment !== "." && segment !== "..");
};

const isRequestedRoot = (value: string): boolean => {
  if (value === "~") return true;
  if (value === "/" || value.includes("\\")) return false;
  const relative = value.startsWith("~/")
    ? value.slice(2)
    : value.startsWith("/")
      ? value.slice(1)
      : undefined;
  return (
    relative !== undefined &&
    relative !== "" &&
    relative.split("/").every((segment) => segment !== "" && segment !== "." && segment !== "..")
  );
};

export const DesktopSshFileScopeIdSchema = CanonicalBoundedTextSchema(
  DESKTOP_SSH_MAX_SCOPE_ID_BYTES,
);

/** Only an explicit OpenSSH config alias may cross from the webview; raw destinations are invalid. */
export const DesktopSshAliasSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.makeFilter(
    (value) =>
      (utf8ByteLength(value) <= DESKTOP_SSH_MAX_ALIAS_BYTES &&
        /^[A-Za-z0-9._+-]+$/u.test(value) &&
        !value.startsWith("-")) ||
      "must be an explicit OpenSSH config alias",
  ),
);

export const DesktopSshRequestedRootSchema = BoundedPathTextSchema(
  DESKTOP_SSH_MAX_ROOT_BYTES,
).check(
  Schema.makeFilter(
    (value) => isRequestedRoot(value) || "must be an absolute remote directory or ~/ path",
  ),
);

export const DesktopSshCanonicalRootSchema = BoundedPathTextSchema(
  DESKTOP_SSH_MAX_ROOT_BYTES,
).check(
  Schema.makeFilter(
    (value) => isCanonicalAbsoluteRoot(value) || "must be a canonical absolute remote root",
  ),
);

export const DesktopSshRelativeDirectoryPathSchema = BoundedPathTextSchema(
  DESKTOP_SSH_MAX_RELATIVE_PATH_BYTES,
  true,
).check(
  Schema.makeFilter(
    (value) =>
      isCanonicalPath(value, true) || "must be an empty or canonical relative directory path",
  ),
);

export const DesktopSshRelativeFilePathSchema = BoundedPathTextSchema(
  DESKTOP_SSH_MAX_RELATIVE_PATH_BYTES,
).check(
  Schema.makeFilter(
    (value) => isCanonicalPath(value, false) || "must be a canonical relative file path",
  ),
);

export const DesktopSshFileNameSchema = BoundedPathTextSchema(DESKTOP_SSH_MAX_NAME_BYTES).check(
  Schema.makeFilter(
    (value) =>
      (value !== "." && value !== ".." && !value.includes("/") && !value.includes("\\")) ||
      "must be one canonical file name",
  ),
);

export const DesktopSshCursorSchema = DesktopSshFileNameSchema;
export const DesktopSshSessionIdSchema = Schema.String.check(
  Schema.makeFilter(
    (value) => /^sshfs_[A-Za-z0-9_-]{1,58}$/u.test(value) || "must be a native SSH file session ID",
  ),
);
export const DesktopSshHostDiscoveryIdSchema = Schema.String.check(
  Schema.makeFilter(
    (value) =>
      /^sshcfg_[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u.test(value) ||
      "must be a native SSH host-discovery ID",
  ),
);
export const DesktopSshListLimitSchema = Schema.Int.check(
  Schema.isBetween({ minimum: 1, maximum: DESKTOP_SSH_MAX_LIST_LIMIT }),
);
export const DesktopSshTextByteLimitSchema = Schema.Int.check(
  Schema.isBetween({ minimum: 1, maximum: DESKTOP_SSH_MAX_TEXT_BYTES }),
);

export const DesktopSshResolvedHostnameSchema = CanonicalBoundedTextSchema(255).check(
  Schema.makeFilter(
    (value) =>
      (/^[A-Za-z0-9._:%-]+$/u.test(value) && !value.startsWith("-")) ||
      "must be the resolved SSH hostname",
  ),
);
export const DesktopSshResolvedUsernameSchema = CanonicalBoundedTextSchema(255).check(
  Schema.makeFilter(
    (value) => /^[A-Za-z0-9._-]+$/u.test(value) || "must be the resolved SSH username",
  ),
);
export const DesktopSshResolvedPortSchema = Schema.Int.check(
  Schema.isBetween({ minimum: 1, maximum: 65_535 }),
);

export const DesktopSshFileScopeSchema = Schema.Struct({
  workspaceId: DesktopSshFileScopeIdSchema,
  projectId: DesktopSshFileScopeIdSchema,
});

export const DesktopSshDiscoveredHostSchema = Schema.Struct({
  alias: DesktopSshAliasSchema,
});

export const DesktopSshDiscoveredHostsSchema = Schema.Array(DesktopSshDiscoveredHostSchema).check(
  Schema.isMaxLength(DESKTOP_SSH_MAX_DISCOVERED_HOSTS),
  Schema.makeFilter((hosts) => {
    let previous: string | undefined;
    for (const host of hosts) {
      if (previous !== undefined && compareUtf8Text(host.alias, previous) <= 0) {
        return "must contain unique aliases in ascending order";
      }
      previous = host.alias;
    }
    return true;
  }),
);

export const DesktopSshDiscoverHostsInputSchema = Schema.Struct({
  scope: DesktopSshFileScopeSchema,
});

export const DesktopSshHostDiscoverySchema = Schema.Struct({
  version: Schema.Literal(1),
  scope: DesktopSshFileScopeSchema,
  discoveryId: DesktopSshHostDiscoveryIdSchema,
  hosts: DesktopSshDiscoveredHostsSchema,
});

export const DesktopSshFileSessionRefSchema = Schema.Struct({
  scope: DesktopSshFileScopeSchema,
  sessionId: DesktopSshSessionIdSchema,
});

export const DesktopSshFileConnectInputSchema = Schema.Struct({
  scope: DesktopSshFileScopeSchema,
  discoveryId: DesktopSshHostDiscoveryIdSchema,
  alias: DesktopSshAliasSchema,
  root: DesktopSshRequestedRootSchema,
});

export const DesktopSshFileSessionSchema = Schema.Struct({
  version: Schema.Literal(1),
  scope: DesktopSshFileScopeSchema,
  sessionId: DesktopSshSessionIdSchema,
  discoveryId: DesktopSshHostDiscoveryIdSchema,
  alias: DesktopSshAliasSchema,
  root: DesktopSshCanonicalRootSchema,
  hostname: DesktopSshResolvedHostnameSchema,
  username: DesktopSshResolvedUsernameSchema,
  port: DesktopSshResolvedPortSchema,
});

const DesktopSshDirectoryEntryKindSchema = Schema.Literal("directory");
const DesktopSshNonDirectoryEntryKindSchema = Schema.Literals(["file", "symlink", "other"]);
export const DesktopSshFileEntryKindSchema = Schema.Union([
  DesktopSshDirectoryEntryKindSchema,
  DesktopSshNonDirectoryEntryKindSchema,
]);

const DesktopSshFileEntryFields = {
  name: DesktopSshFileNameSchema,
  relativePath: DesktopSshRelativeFilePathSchema,
} as const;

export const DesktopSshFileEntrySchema = Schema.Union([
  Schema.Struct({
    ...DesktopSshFileEntryFields,
    kind: DesktopSshDirectoryEntryKindSchema,
  }),
  Schema.Struct({
    ...DesktopSshFileEntryFields,
    kind: DesktopSshNonDirectoryEntryKindSchema,
    sizeBytes: NonNegativeSafeIntegerSchema,
  }),
]);

export const DesktopSshListDirectoryInputSchema = Schema.Struct({
  ...DesktopSshFileSessionRefSchema.fields,
  relativePath: DesktopSshRelativeDirectoryPathSchema,
  cursor: Schema.optionalKey(DesktopSshCursorSchema),
  limit: DesktopSshListLimitSchema,
});

export const DesktopSshListDirectoryResultSchema = Schema.Struct({
  version: Schema.Literal(1),
  ...DesktopSshFileSessionRefSchema.fields,
  directory: DesktopSshRelativeDirectoryPathSchema,
  entries: Schema.Array(DesktopSshFileEntrySchema).check(
    Schema.isMaxLength(DESKTOP_SSH_MAX_LIST_LIMIT),
  ),
  nextCursor: Schema.optionalKey(DesktopSshCursorSchema),
}).check(
  Schema.makeFilter((result) => {
    let previous: string | undefined;
    for (const entry of result.entries) {
      const expectedPath = result.directory ? `${result.directory}/${entry.name}` : entry.name;
      if (
        entry.relativePath !== expectedPath ||
        (previous !== undefined && compareUtf8Text(entry.name, previous) <= 0)
      ) {
        return "must contain unique ascending direct children of the requested directory";
      }
      previous = entry.name;
    }
    return (
      result.nextCursor === undefined ||
      (result.entries.length > 0 && result.nextCursor === result.entries.at(-1)?.name) ||
      "next cursor must identify the last returned entry"
    );
  }),
);

export const DesktopSshReadTextFileInputSchema = Schema.Struct({
  ...DesktopSshFileSessionRefSchema.fields,
  relativePath: DesktopSshRelativeFilePathSchema,
  maxBytes: DesktopSshTextByteLimitSchema,
});

export const DesktopSshReadTextFileResultSchema = Schema.Struct({
  version: Schema.Literal(1),
  ...DesktopSshFileSessionRefSchema.fields,
  relativePath: DesktopSshRelativeFilePathSchema,
  content: Schema.String,
  encoding: Schema.Literal("utf-8"),
  sizeBytes: NonNegativeSafeIntegerSchema.check(
    Schema.isLessThanOrEqualTo(DESKTOP_SSH_MAX_TEXT_BYTES),
  ),
}).check(
  Schema.makeFilter(
    (result) =>
      utf8ByteLength(result.content) === result.sizeBytes ||
      "sizeBytes must equal the UTF-8 content byte length",
  ),
);

export const DesktopSshFileErrorCodeSchema = Schema.Literals([
  "alias-not-authorized",
  "authentication-failed",
  "config-unavailable",
  "connection-failed",
  "directory-too-large",
  "file-changed",
  "file-too-large",
  "invalid-input",
  "invalid-cursor",
  "invalid-limit",
  "invalid-path",
  "invalid-response",
  "invalid-root",
  "invalid-scope",
  "invalid-session",
  "not-directory",
  "not-file",
  "not-found",
  "not-utf8",
  "operation-failed",
  "output-too-large",
  "path-escape",
  "permission-denied",
  "protocol-error",
  "remote-helper-unavailable",
  "scope-mismatch",
  "session-not-found",
  "symlink-denied",
  "timeout",
  "unavailable",
  "unsupported-name",
]);

export const DesktopSshFileErrorPayloadSchema = Schema.Struct({
  code: DesktopSshFileErrorCodeSchema,
  message: CanonicalBoundedTextSchema(240),
  retryable: Schema.Boolean,
});

export const DesktopSshCloseResultSchema = Schema.Null;

export type DesktopSshFileScope = typeof DesktopSshFileScopeSchema.Type;
export type DesktopSshFileScopeEncoded = typeof DesktopSshFileScopeSchema.Encoded;
export type DesktopSshDiscoveredHost = typeof DesktopSshDiscoveredHostSchema.Type;
export type DesktopSshDiscoveredHostEncoded = typeof DesktopSshDiscoveredHostSchema.Encoded;
export type DesktopSshDiscoveredHosts = typeof DesktopSshDiscoveredHostsSchema.Type;
export type DesktopSshDiscoveredHostsEncoded = typeof DesktopSshDiscoveredHostsSchema.Encoded;
export type DesktopSshDiscoverHostsInput = typeof DesktopSshDiscoverHostsInputSchema.Type;
export type DesktopSshDiscoverHostsInputEncoded = typeof DesktopSshDiscoverHostsInputSchema.Encoded;
export type DesktopSshHostDiscovery = typeof DesktopSshHostDiscoverySchema.Type;
export type DesktopSshHostDiscoveryEncoded = typeof DesktopSshHostDiscoverySchema.Encoded;
export type DesktopSshFileSessionRef = typeof DesktopSshFileSessionRefSchema.Type;
export type DesktopSshFileSessionRefEncoded = typeof DesktopSshFileSessionRefSchema.Encoded;
export type DesktopSshFileConnectInput = typeof DesktopSshFileConnectInputSchema.Type;
export type DesktopSshFileConnectInputEncoded = typeof DesktopSshFileConnectInputSchema.Encoded;
export type DesktopSshFileSession = typeof DesktopSshFileSessionSchema.Type;
export type DesktopSshFileSessionEncoded = typeof DesktopSshFileSessionSchema.Encoded;
export type DesktopSshFileEntryKind = typeof DesktopSshFileEntryKindSchema.Type;
export type DesktopSshFileEntry = typeof DesktopSshFileEntrySchema.Type;
export type DesktopSshFileEntryEncoded = typeof DesktopSshFileEntrySchema.Encoded;
export type DesktopSshListDirectoryInput = typeof DesktopSshListDirectoryInputSchema.Type;
export type DesktopSshListDirectoryInputEncoded = typeof DesktopSshListDirectoryInputSchema.Encoded;
export type DesktopSshListDirectoryResult = typeof DesktopSshListDirectoryResultSchema.Type;
export type DesktopSshListDirectoryResultEncoded =
  typeof DesktopSshListDirectoryResultSchema.Encoded;
export type DesktopSshReadTextFileInput = typeof DesktopSshReadTextFileInputSchema.Type;
export type DesktopSshReadTextFileInputEncoded = typeof DesktopSshReadTextFileInputSchema.Encoded;
export type DesktopSshReadTextFileResult = typeof DesktopSshReadTextFileResultSchema.Type;
export type DesktopSshReadTextFileResultEncoded = typeof DesktopSshReadTextFileResultSchema.Encoded;
export type DesktopSshFileErrorCode = typeof DesktopSshFileErrorCodeSchema.Type;
export type DesktopSshFileErrorPayload = typeof DesktopSshFileErrorPayloadSchema.Type;
export type DesktopSshFileErrorPayloadEncoded = typeof DesktopSshFileErrorPayloadSchema.Encoded;

export const decodeDesktopSshDiscoveredHosts = Schema.decodeUnknownSync(
  DesktopSshDiscoveredHostsSchema,
  DesktopSshFileBoundaryParseOptions,
);
export const encodeDesktopSshDiscoveredHosts = Schema.encodeSync(
  DesktopSshDiscoveredHostsSchema,
  DesktopSshFileBoundaryParseOptions,
);
export const decodeDesktopSshDiscoverHostsInput = Schema.decodeUnknownSync(
  DesktopSshDiscoverHostsInputSchema,
  DesktopSshFileBoundaryParseOptions,
);
export const encodeDesktopSshDiscoverHostsInput = Schema.encodeSync(
  DesktopSshDiscoverHostsInputSchema,
  DesktopSshFileBoundaryParseOptions,
);
export const decodeDesktopSshHostDiscovery = Schema.decodeUnknownSync(
  DesktopSshHostDiscoverySchema,
  DesktopSshFileBoundaryParseOptions,
);
export const encodeDesktopSshHostDiscovery = Schema.encodeSync(
  DesktopSshHostDiscoverySchema,
  DesktopSshFileBoundaryParseOptions,
);
export const decodeDesktopSshFileSessionRef = Schema.decodeUnknownSync(
  DesktopSshFileSessionRefSchema,
  DesktopSshFileBoundaryParseOptions,
);
export const encodeDesktopSshFileSessionRef = Schema.encodeSync(
  DesktopSshFileSessionRefSchema,
  DesktopSshFileBoundaryParseOptions,
);
export const decodeDesktopSshFileConnectInput = Schema.decodeUnknownSync(
  DesktopSshFileConnectInputSchema,
  DesktopSshFileBoundaryParseOptions,
);
export const encodeDesktopSshFileConnectInput = Schema.encodeSync(
  DesktopSshFileConnectInputSchema,
  DesktopSshFileBoundaryParseOptions,
);
export const decodeDesktopSshFileSession = Schema.decodeUnknownSync(
  DesktopSshFileSessionSchema,
  DesktopSshFileBoundaryParseOptions,
);
export const encodeDesktopSshFileSession = Schema.encodeSync(
  DesktopSshFileSessionSchema,
  DesktopSshFileBoundaryParseOptions,
);
export const decodeDesktopSshFileEntry = Schema.decodeUnknownSync(
  DesktopSshFileEntrySchema,
  DesktopSshFileBoundaryParseOptions,
);
export const encodeDesktopSshFileEntry = Schema.encodeSync(
  DesktopSshFileEntrySchema,
  DesktopSshFileBoundaryParseOptions,
);
export const decodeDesktopSshListDirectoryInput = Schema.decodeUnknownSync(
  DesktopSshListDirectoryInputSchema,
  DesktopSshFileBoundaryParseOptions,
);
export const encodeDesktopSshListDirectoryInput = Schema.encodeSync(
  DesktopSshListDirectoryInputSchema,
  DesktopSshFileBoundaryParseOptions,
);
export const decodeDesktopSshListDirectoryResult = Schema.decodeUnknownSync(
  DesktopSshListDirectoryResultSchema,
  DesktopSshFileBoundaryParseOptions,
);
export const encodeDesktopSshListDirectoryResult = Schema.encodeSync(
  DesktopSshListDirectoryResultSchema,
  DesktopSshFileBoundaryParseOptions,
);
export const decodeDesktopSshReadTextFileInput = Schema.decodeUnknownSync(
  DesktopSshReadTextFileInputSchema,
  DesktopSshFileBoundaryParseOptions,
);
export const encodeDesktopSshReadTextFileInput = Schema.encodeSync(
  DesktopSshReadTextFileInputSchema,
  DesktopSshFileBoundaryParseOptions,
);
export const decodeDesktopSshReadTextFileResult = Schema.decodeUnknownSync(
  DesktopSshReadTextFileResultSchema,
  DesktopSshFileBoundaryParseOptions,
);
export const encodeDesktopSshReadTextFileResult = Schema.encodeSync(
  DesktopSshReadTextFileResultSchema,
  DesktopSshFileBoundaryParseOptions,
);
export const decodeDesktopSshFileErrorPayload = Schema.decodeUnknownSync(
  DesktopSshFileErrorPayloadSchema,
  DesktopSshFileBoundaryParseOptions,
);
export const encodeDesktopSshFileErrorPayload = Schema.encodeSync(
  DesktopSshFileErrorPayloadSchema,
  DesktopSshFileBoundaryParseOptions,
);
export const decodeDesktopSshCloseResult = Schema.decodeUnknownSync(
  DesktopSshCloseResultSchema,
  DesktopSshFileBoundaryParseOptions,
);
export const encodeDesktopSshCloseResult = Schema.encodeSync(
  DesktopSshCloseResultSchema,
  DesktopSshFileBoundaryParseOptions,
);
